import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  FormGroup,
  FormInput,
  NavButtons,
} from '../common';
import { api, getAuthHeaders } from '../../api/api';
import { apiUrl } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ensureAiConfig } from '../../utils/aiConfig';
import {
  normalizeTextTemplateCta,
  normalizeTextTemplateSections,
  applyTextTemplateToSettings,
} from '../../utils/textTemplateSync';
import { deriveDefaultsFromGlobals } from '../../utils/defaults';
import { mapTemplateToConfigurator } from '../../utils/templateMapping';
import { buildTemplatePayload } from '../../utils/templatePayload';
import { writeSelectedEventScope } from '../../utils/eventScopeStorage';

const parseVisibilityGroupId = visibility => {
  if (typeof visibility !== 'string' || !visibility.startsWith('group-')) return null;
  const parsed = Number(visibility.slice(6));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const LEGACY_FOOTER_PLACEHOLDER = '© EVENT 2026 - All rights reserved.';
const LEGACY_SALUTATION_DEFAULT = String.raw`Dear \${p.firstName} \${p.lastName},`;
const LEGACY_CLOSING_DEFAULT = 'Sincerely,';
const LEGACY_SALUTATION_TRANSLATIONS = {
  en: LEGACY_SALUTATION_DEFAULT,
  de: String.raw`Guten Tag \${p.firstName} \${p.lastName},`,
  fr: String.raw`Chère/Cher \${p.firstName} \${p.lastName},`,
  ar: '&{anrede}،',
};
const LEGACY_CLOSING_TRANSLATIONS = {
  en: 'Sincerely,',
  de: 'Mit freundlichen Grüßen',
  fr: 'Cordialement,',
  ar: 'مع أطيب التحيات،',
};

const isExactLegacyTranslationMap = (value, legacyMap) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const valueKeys = Object.keys(value);
  const legacyKeys = Object.keys(legacyMap);
  if (valueKeys.length !== legacyKeys.length) return false;
  return legacyKeys.every(key => value[key] === legacyMap[key]);
};

const emptyTemplate = {
  name: '',
  role: 'registration',
  visibility: '',
  saveScope: 'event',
  targetGroupId: null,
  content: '',
  languages: [],
  sections: [],
};

const SECTION_MARKER_REGEX = /\[\[SECTION:\s*([^\]]+)\s*\]\]/gi;
const CTA_MARKER_REGEX = /\[\[CTA:\s*([^\]]+)\s*\]\]/gi;
const QUICK_INSERT_CARD_CLASS =
  'group rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-primary hover:text-primary';

const parseCtaMarker = raw => {
  const result = {
    type: 'primary',
    text: '',
    url: '',
  };
  if (!raw) return result;
  const parts = raw
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  parts.forEach(part => {
    const lower = part.toLowerCase();
    if (['primary', 'secondary', 'all'].includes(lower)) {
      result.type = lower;
      return;
    }
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1) return;
    const key = part.slice(0, colonIndex).trim().toLowerCase();
    const value = part.slice(colonIndex + 1).trim();
    if (key === 'text') result.text = value;
    if (key === 'url') result.url = value;
  });
  return result;
};

const formatCtaMarker = (type, text, url) => {
  const safeText = (text || '').trim() || 'buttontext';
  const safeUrl = (url || '').trim() || 'changeme';
  const parts = [];
  if (type && type !== 'primary') parts.push(type);
  parts.push(`text:${safeText}`);
  parts.push(`url:${safeUrl}`);
  return `[[CTA: ${parts.join(', ')}]]`;
};

const REGMAIL_VARIABLES = [
  { key: 'eventName', label: 'Event Name' },
  { key: 'hostingOrganisation', label: 'Hosting Organisation' },
  { key: 'eventLocation', label: 'Event Location' },
  { key: 'eventStartDate', label: 'Event Start Date' },
  { key: 'eventEndDate', label: 'Event End Date' },
  { key: 'eventTime', label: 'Event Time' },
  { key: 'paymentDueDate', label: 'Payment Due Date' },
  { key: 'eventEmail', label: 'Event Email' },
];

const QUICK_LINK_BUTTONS = [
  {
    title: 'Web link',
    snippet: '[web](https://{{websiteUrl}})\n',
    preview: '[web](https://{{websiteUrl}})',
    helper: 'Inserts a website link placeholder.',
  },
  {
    title: 'Location link',
    snippet: '[location](https://maps.google.com/?q={{eventLocation}})\n',
    preview: '[location](https://maps.google.com/?q={{eventLocation}})',
    helper: 'Inserts a Google Maps link placeholder for the event location.',
  },
  {
    title: 'Telephone link',
    snippet: '[telephone](tel:{{contactPhone}})\n',
    preview: '[telephone](tel:{{contactPhone}})',
    helper: 'Inserts a click-to-call link placeholder.',
  },
  {
    title: 'Email link',
    snippet: '[email](mailto:{{contactEmail}})\n',
    preview: '[email](mailto:{{contactEmail}})',
    helper: 'Inserts a mailto link placeholder.',
  },
];

const STORAGE_KEY = 'textTemplateSectionState';
const FAVORITES_STORAGE_KEY = 'textTemplateFavoritesByUser';
const HIDDEN_EVENTS_KEY = 'hidden_events_by_user';
const normalizeVariableKey = value => {
  const source = String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');
  const normalized = source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return normalized;
};

const readHiddenEvents = userId => {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(HIDDEN_EVENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const list = parsed && typeof parsed === 'object' ? parsed[String(userId)] : [];
    return Array.isArray(list)
      ? list.map(id => Number(id)).filter(Number.isFinite)
      : [];
  } catch {
    return [];
  }
};

const writeHiddenEvents = (userId, ids) => {
  if (!userId) return;
  try {
    const raw = localStorage.getItem(HIDDEN_EVENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[String(userId)] = ids;
    localStorage.setItem(HIDDEN_EVENTS_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
};
const readFavoritesByUser = () => {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};
const writeFavoritesByUser = next => {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(next || {}));
  } catch {
    // ignore storage write failures
  }
};

export const TextTemplatesSection = ({
  onNavigate,
  showNotification,
  events = [],
  globalSettings,
  activeTemplateEventId = null,
}) => {
  const { user, activeGroupId } = useAuth();
  const [selectedEventId, setSelectedEventId] = useState(events[0]?.id || null);
  const [variables, setVariables] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(emptyTemplate);
  const [editingId, setEditingId] = useState(null);
  const [layoutText, setLayoutText] = useState('');
  const [batchValues, setBatchValues] = useState({});
  const [batchLanguageSelections, setBatchLanguageSelections] = useState({});
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [favoriteTemplateIds, setFavoriteTemplateIds] = useState([]);
  const [templateFilter, setTemplateFilter] = useState('');
  const [generated, setGenerated] = useState([]);
  const [targetEventForSave, setTargetEventForSave] = useState(events[0]?.id || null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [designTemplates, setDesignTemplates] = useState([]);
  const [designTemplatesLoading, setDesignTemplatesLoading] = useState(false);
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [savingGenerated, setSavingGenerated] = useState(false);
  const [saveSummary, setSaveSummary] = useState(null);
  const [editingVariableId, setEditingVariableId] = useState(null);
  const [accessibleGroups, setAccessibleGroups] = useState([]);
  const [variableGroupId, setVariableGroupId] = useState(null);
  const [aiResponses, setAiResponses] = useState([]);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [activeAiResponseId, setActiveAiResponseId] = useState(null);
  const [activeLibraryGroup, setActiveLibraryGroup] = useState('all');
  const [showArchivedEvents, setShowArchivedEvents] = useState(false);
  const [showHiddenEvents, setShowHiddenEvents] = useState(false);
  const [hiddenEventIds, setHiddenEventIds] = useState([]);
  const [aiModalBusy, setAiModalBusy] = useState(false);
  const [lastAiAction, setLastAiAction] = useState({
    command: 'improve',
    label: 'Improve',
    language: '',
    languageLabel: '',
    customPrompt: '',
  });

  const extractTemplateData = useCallback(template => {
    if (!template?.data) return null;
    if (typeof template.data === 'string') {
      try {
        return JSON.parse(template.data);
      } catch {
        return null;
      }
    }
    if (typeof template.data === 'object') return template.data;
    return null;
  }, []);

  const isBaseDesignTemplate = useCallback(template => {
    const data = extractTemplateData(template);
    return Boolean(data?.baseDesign);
  }, [extractTemplateData]);
  const aiMenuRef = useRef(null);
  const aiTriggerRef = useRef(null);
  const layoutRef = useRef(null);
  const aiResponseRef = useRef(null);
  const layoutSelectionRef = useRef({ start: null, end: null });
  const aiConfig = useMemo(
    () => ensureAiConfig(globalSettings?.aiConfig),
    [globalSettings?.aiConfig]
  );
  const quickActionGroups = aiConfig.textTemplateQuickActions || [];
  const translateLanguages = aiConfig.languages || [];
  const isAdmin = user?.role?.name === 'Admin';
  const userGroupIds = useMemo(() => {
    const ids = new Set();
    const fromPrimary = Number(user?.group?.id || user?.group_id || user?.groupId);
    if (Number.isFinite(fromPrimary) && fromPrimary > 0) ids.add(fromPrimary);
    if (Array.isArray(user?.groups)) {
      user.groups.forEach(group => {
        const parsed = Number(group?.id || group?.group_id || group?.groupId);
        if (Number.isFinite(parsed) && parsed > 0) ids.add(parsed);
      });
    }
    return Array.from(ids);
  }, [user]);
  const activeGroupIdNumber = useMemo(() => {
    const parsed = Number(activeGroupId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [activeGroupId]);
  const primaryGroupId = activeGroupIdNumber || userGroupIds[0] || null;
  const userGroupIdSet = useMemo(() => new Set(userGroupIds), [userGroupIds]);
  const effectiveGroupIdSet = useMemo(() => {
    if (!isAdmin && activeGroupIdNumber && userGroupIdSet.has(activeGroupIdNumber)) {
      return new Set([activeGroupIdNumber]);
    }
    return userGroupIdSet;
  }, [activeGroupIdNumber, isAdmin, userGroupIdSet]);
  const visibleGroups = useMemo(() => {
    if (isAdmin) return accessibleGroups;
    return (accessibleGroups || []).filter(group =>
      userGroupIdSet.has(Number(group?.id))
    );
  }, [accessibleGroups, isAdmin, userGroupIdSet]);
  const canSelectTargetGroup = isAdmin || visibleGroups.length > 1;
  const canSwitchLibraryGroup = isAdmin || visibleGroups.length > 1;
  const selectableEvents = useMemo(() => {
    if (isAdmin) return events;
    return events.filter(
      evt => String(evt?.name || '').toLowerCase() !== 'global'
    );
  }, [events, isAdmin]);
  const hiddenEventIdSet = useMemo(
    () => new Set(hiddenEventIds.map(id => Number(id)).filter(Number.isFinite)),
    [hiddenEventIds]
  );
  const visibleSelectableEvents = useMemo(() => {
    return (selectableEvents || []).filter(evt => {
      const isArchived = evt.status === 'archived';
      const isHidden = hiddenEventIdSet.has(Number(evt.id));
      if (!showArchivedEvents && isArchived) return false;
      if (!showHiddenEvents && isHidden) return false;
      return true;
    });
  }, [selectableEvents, hiddenEventIdSet, showArchivedEvents, showHiddenEvents]);
  const activeAiResponse = useMemo(() => {
    if (!aiResponses.length) return null;
    const match = aiResponses.find(item => item.id === activeAiResponseId);
    return match || aiResponses[0];
  }, [aiResponses, activeAiResponseId]);
  const favoritesUserKey = useMemo(() => {
    const parsed = Number(user?.id);
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : null;
  }, [user?.id]);

  useEffect(() => {
    if (!aiResponses.length) {
      setActiveAiResponseId(null);
      return;
    }
    if (!activeAiResponseId || !aiResponses.some(item => item.id === activeAiResponseId)) {
      setActiveAiResponseId(aiResponses[0].id);
    }
  }, [aiResponses, activeAiResponseId]);

  useEffect(() => {
    if (isAdmin) return;
    if (
      selectedEventId &&
      !visibleSelectableEvents.some(evt => evt.id === selectedEventId)
    ) {
      setSelectedEventId(visibleSelectableEvents[0]?.id || null);
    }
    if (
      targetEventForSave &&
      !visibleSelectableEvents.some(evt => evt.id === targetEventForSave)
    ) {
      setTargetEventForSave(visibleSelectableEvents[0]?.id || null);
    }
  }, [isAdmin, visibleSelectableEvents, selectedEventId, targetEventForSave]);

  useEffect(() => {
    if (!activeTemplateEventId) return;
    const parsed = Number(activeTemplateEventId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    if (!visibleSelectableEvents.some(evt => Number(evt.id) === parsed)) return;
    setSelectedEventId(prev => (Number(prev) === parsed ? prev : parsed));
  }, [activeTemplateEventId, visibleSelectableEvents]);

  useEffect(() => {
    if (isAdmin) {
      if (!variableGroupId && visibleGroups[0]?.id) {
        const preferredGroupId =
          activeGroupIdNumber ||
          Number(visibleGroups[0].id);
        setVariableGroupId(preferredGroupId);
        setDraft(prev => ({
          ...prev,
          targetGroupId: prev.targetGroupId || preferredGroupId,
        }));
      }
      return;
    }
    if (visibleGroups[0]?.id) {
      const preferredGroupId =
        activeGroupIdNumber ||
        Number(visibleGroups[0].id);
      setVariableGroupId(preferredGroupId);
      setDraft(prev => ({
        ...prev,
        targetGroupId:
          visibleGroups.length === 1
            ? preferredGroupId
            : prev.targetGroupId || preferredGroupId,
      }));
    }
  }, [isAdmin, variableGroupId, visibleGroups, activeGroupIdNumber]);

  useEffect(() => {
    if (!visibleGroups.length) return;
    const allowedIds = new Set(visibleGroups.map(group => Number(group.id)));
    setDraft(prev => {
      const current = Number(prev.targetGroupId);
      if (Number.isFinite(current) && allowedIds.has(current)) return prev;
      const preferred =
        activeGroupIdNumber && allowedIds.has(activeGroupIdNumber)
          ? activeGroupIdNumber
          : Number(visibleGroups[0].id) || null;
      return { ...prev, targetGroupId: preferred };
    });
  }, [visibleGroups, activeGroupIdNumber]);

  useEffect(() => {
    if (!canSwitchLibraryGroup) {
      setActiveLibraryGroup('all');
      return;
    }
    if (activeLibraryGroup === 'all') return;
    const nextId = Number(activeLibraryGroup);
    if (!Number.isFinite(nextId)) {
      setActiveLibraryGroup('all');
      return;
    }
    if (!isAdmin && !userGroupIdSet.has(nextId)) {
      setActiveLibraryGroup('all');
    }
  }, [activeLibraryGroup, canSwitchLibraryGroup, isAdmin, userGroupIdSet]);

  // Restore persisted UI state
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.batchValues) setBatchValues(parsed.batchValues);
      if (parsed.batchLanguageSelections) {
        setBatchLanguageSelections(parsed.batchLanguageSelections);
      }
      if (parsed.generated) setGenerated(parsed.generated);
      if (parsed.targetEventForSave) setTargetEventForSave(parsed.targetEventForSave);
      if (parsed.selectedTemplateIds) setSelectedTemplateIds(parsed.selectedTemplateIds);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!favoritesUserKey) {
      setFavoriteTemplateIds([]);
      return;
    }
    const parsed = readFavoritesByUser();
    const list = Array.isArray(parsed?.[favoritesUserKey])
      ? parsed[favoritesUserKey]
      : [];
    setFavoriteTemplateIds(
      list.map(id => Number(id)).filter(Number.isFinite)
    );
  }, [favoritesUserKey]);

  useEffect(() => {
    if (!favoritesUserKey) return;
    const parsed = readFavoritesByUser();
    parsed[favoritesUserKey] = favoriteTemplateIds;
    writeFavoritesByUser(parsed);
  }, [favoriteTemplateIds, favoritesUserKey]);

  useEffect(() => {
    if (!user?.id) return;
    setHiddenEventIds(readHiddenEvents(user.id));
  }, [user?.id]);

  useEffect(() => {
    if (!canSwitchLibraryGroup) return;
    if (!activeGroupIdNumber) return;
    if (!visibleGroups.some(group => Number(group.id) === activeGroupIdNumber)) return;
    setActiveLibraryGroup(String(activeGroupIdNumber));
  }, [activeGroupIdNumber, canSwitchLibraryGroup, visibleGroups]);

  // Persist UI state so it survives navigation
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          batchValues,
          batchLanguageSelections,
          generated,
          targetEventForSave,
          selectedTemplateIds,
        })
      );
    } catch {
      /* ignore */
    }
  }, [batchValues, batchLanguageSelections, generated, targetEventForSave, selectedTemplateIds]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const ensureRegmailDefaults = async currentVars => {
          if (!primaryGroupId) return currentVars;
          const existingKeys = new Set((currentVars || []).map(v => v.key));
          const missing = REGMAIL_VARIABLES.filter(v => !existingKeys.has(v.key));
          if (!missing.length) return currentVars;
          const created = [];
          for (const v of missing) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const item = await api.createEventVariable(
                { scope: 'group', groupId: primaryGroupId },
                { ...v }
              );
              created.push(item);
            } catch {}
          }
          const merged = [...(currentVars || []), ...created];
          return merged.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
        };

        const [tpls, groups] = await Promise.all([
          api.listTextTemplates({}),
          api.listAccessibleGroups().catch(() => []),
        ]);
        const normalizedGroups = Array.isArray(groups) ? groups : [];
        const filteredGroups = isAdmin
          ? normalizedGroups
          : normalizedGroups.filter(group =>
              userGroupIdSet.has(Number(group?.id))
            );
        setAccessibleGroups(normalizedGroups);

        const groupIds = Array.from(
          new Set(
            filteredGroups
              .map(group => Number(group?.id))
              .filter(id => Number.isFinite(id) && id > 0)
          )
        );
        const varsByGroup = await Promise.all(
          groupIds.map(async id => {
            try {
              const items = await api.listEventVariables({ scope: 'group', groupId: id });
              return items.map(item => ({
                ...item,
                group_id: Number(item.group_id || id),
              }));
            } catch {
              return [];
            }
          })
        );
        const flatVars = varsByGroup.flat();
        const ownGroupVars = flatVars.filter(
          item => Number(item.group_id) === Number(primaryGroupId)
        );
        const ensuredOwnGroupVars = await ensureRegmailDefaults(ownGroupVars || []);
        const mergedVarMap = new Map();
        [...flatVars, ...ensuredOwnGroupVars].forEach(item => {
          if (!item?.id) return;
          mergedVarMap.set(item.id, item);
        });
        const groupNameById = new Map(
          filteredGroups.map(group => [Number(group.id), group.name || `Group ${group.id}`])
        );
        const mergedVars = Array.from(mergedVarMap.values())
          .map(item => ({
            ...item,
            sourceGroupName:
              groupNameById.get(Number(item.group_id)) ||
              item?.Group?.name ||
              `Group ${item.group_id}`,
          }))
          .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
        setVariables(mergedVars);
        setTemplates(tpls || []);
      } catch (err) {
        showNotification?.(err.message || 'Failed to load templates', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [primaryGroupId, showNotification, isAdmin, userGroupIdSet, activeGroupIdNumber]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        aiMenuOpen &&
        aiMenuRef.current &&
        !aiMenuRef.current.contains(event.target) &&
        !aiTriggerRef.current?.contains(event.target)
      ) {
        setAiMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [aiMenuOpen]);

  const buildLayoutText = source => {
    const intro = (source?.content || '').trim();
    const sections = Array.isArray(source?.sections) ? source.sections : [];
    const blocks = [];
    if (intro) blocks.push(intro);
    sections.forEach(section => {
      const title = section?.title || 'Section title';
      const body = section?.body || '';
      const ctaValue = normalizeTextTemplateCta(section?.cta);
      blocks.push('', `[[SECTION: ${title}]]`);
      if (body) blocks.push(body);
      if (ctaValue !== 'none') {
        if (ctaValue === 'all') {
          blocks.push(
            formatCtaMarker('primary', section?.ctaText, section?.ctaUrl),
            formatCtaMarker(
              'secondary',
              section?.ctaTextSecondary || section?.ctaText,
              section?.ctaUrlSecondary || section?.ctaUrl
            )
          );
        } else if (ctaValue === 'secondary') {
          blocks.push(
            formatCtaMarker(
              'secondary',
              section?.ctaTextSecondary || section?.ctaText,
              section?.ctaUrlSecondary || section?.ctaUrl
            )
          );
        } else {
          blocks.push(formatCtaMarker('primary', section?.ctaText, section?.ctaUrl));
        }
      }
    });
    return blocks.join('\n').trim();
  };

  const parseLayoutText = (text, existingSections = []) => {
    const raw = text || '';
    const matches = Array.from(raw.matchAll(SECTION_MARKER_REGEX));
    if (matches.length === 0) {
      return { content: raw.trim(), sections: [] };
    }
    const nextSections = [];
    const intro = raw.slice(0, matches[0].index).trim();
    matches.forEach((match, index) => {
      const title = (match[1] || '').trim();
      const start = match.index + match[0].length;
      const end = matches[index + 1]?.index ?? raw.length;
      const block = raw.slice(start, end);
      const base = existingSections[index] || {};
      const ctaMatches = Array.from(block.matchAll(CTA_MARKER_REGEX));
      let hasPrimary = false;
      let hasSecondary = false;
      let ctaText = base.ctaText || '';
      let ctaUrl = base.ctaUrl || '';
      let ctaTextSecondary = base.ctaTextSecondary || '';
      let ctaUrlSecondary = base.ctaUrlSecondary || '';

      ctaMatches.forEach(matchItem => {
        const parsed = parseCtaMarker(matchItem[1] || '');
        const markerType = parsed.type || 'primary';
        if (markerType === 'secondary') {
          hasSecondary = true;
          if (parsed.text) ctaTextSecondary = parsed.text;
          if (parsed.url) ctaUrlSecondary = parsed.url;
          return;
        }
        if (markerType === 'all') {
          hasPrimary = true;
          hasSecondary = true;
          if (parsed.text) {
            if (!ctaText) ctaText = parsed.text;
            if (!ctaTextSecondary) ctaTextSecondary = parsed.text;
          }
          if (parsed.url) {
            if (!ctaUrl) ctaUrl = parsed.url;
            if (!ctaUrlSecondary) ctaUrlSecondary = parsed.url;
          }
          return;
        }
        hasPrimary = true;
        if (parsed.text) ctaText = parsed.text;
        if (parsed.url) ctaUrl = parsed.url;
      });

      let cta = 'none';
      if (hasPrimary && hasSecondary) cta = 'all';
      else if (hasSecondary) cta = 'secondary';
      else if (hasPrimary) cta = 'primary';
      const body = block.replace(CTA_MARKER_REGEX, '').trim();
      nextSections.push({
        id: base.id || uuidv4(),
        title: title || base.title || 'Section title',
        body,
        cta: normalizeTextTemplateCta(cta),
        ctaText,
        ctaUrl,
        ctaTextSecondary,
        ctaUrlSecondary,
      });
    });
    return { content: intro, sections: nextSections };
  };

  useEffect(() => {
    const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
    if (layoutRef.current && activeElement === layoutRef.current) {
      return;
    }
    setLayoutText(buildLayoutText(draft));
  }, [draft]);

  useEffect(() => {
    if (!saveDialogOpen || !targetEventForSave) return;
    let mounted = true;
    setDesignTemplatesLoading(true);
    const loadTemplates = isAdmin
      ? api.getTemplates(user)
      : api.getTemplatesByEvent(targetEventForSave);
    loadTemplates
      .then(result => {
        if (!mounted) return;
        const list = [...(result?.global || []), ...(result?.user || [])];
        const baseDesignCandidates = isAdmin
          ? list.filter(t => isBaseDesignTemplate(t))
          : list.filter(
              t =>
                isBaseDesignTemplate(t) &&
                Number(t?.event_id) === Number(targetEventForSave)
            );
        setDesignTemplates(baseDesignCandidates);
        const preferredVisibility = primaryGroupId ? `group-${primaryGroupId}` : null;
        const baseMatch =
          baseDesignCandidates.find(
            t => preferredVisibility && t.visibility === preferredVisibility
          ) ||
          baseDesignCandidates.find(t => t.visibility === 'global') ||
          baseDesignCandidates.find(t => t.visibility === 'user') ||
          baseDesignCandidates[0] ||
          null;
        setSelectedDesignId(prevSelected => {
          if (
            prevSelected &&
            baseDesignCandidates.some(t => Number(t.id) === Number(prevSelected))
          ) {
            return prevSelected;
          }
          return baseMatch ? baseMatch.id : null;
        });
      })
      .catch(err => {
        if (!mounted) return;
        showNotification?.(err.message || 'Failed to load design templates', 'error');
      })
      .finally(() => {
        if (!mounted) return;
        setDesignTemplatesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [
    saveDialogOpen,
    targetEventForSave,
    showNotification,
    primaryGroupId,
    isAdmin,
    user,
    isBaseDesignTemplate,
  ]);

  const resetDraft = () => {
    setDraft({
      ...emptyTemplate,
      targetGroupId: isAdmin
        ? variableGroupId || visibleGroups[0]?.id || null
        : visibleGroups[0]?.id || null,
    });
    setEditingId(null);
  };

  const normalizeLayoutText = () => {
    setLayoutText(buildLayoutText(draft));
  };

  const handleLayoutChange = event => {
    const nextValue = event.target.value;
    setLayoutText(nextValue);
    if (typeof event.target.selectionStart === 'number') {
      layoutSelectionRef.current = {
        start: event.target.selectionStart,
        end:
          typeof event.target.selectionEnd === 'number'
            ? event.target.selectionEnd
            : event.target.selectionStart,
      };
    }
    const parsed = parseLayoutText(nextValue, draft.sections || []);
    setDraft(prev => ({
      ...prev,
      content: parsed.content,
      sections: parsed.sections,
    }));
  };

  const rememberLayoutSelection = useCallback(node => {
    if (!node || typeof node.selectionStart !== 'number') return;
    layoutSelectionRef.current = {
      start: node.selectionStart,
      end:
        typeof node.selectionEnd === 'number'
          ? node.selectionEnd
          : node.selectionStart,
    };
  }, []);

  const preserveLayoutSelectionOnMouseDown = useCallback(
    event => {
      event.preventDefault();
      if (layoutRef.current) {
        rememberLayoutSelection(layoutRef.current);
      }
    },
    [rememberLayoutSelection]
  );

  const resolveLayoutSelection = useCallback(
    current => {
      const node = layoutRef.current;
      if (
        node &&
        typeof node.selectionStart === 'number' &&
        typeof node.selectionEnd === 'number' &&
        document.activeElement === node
      ) {
        return { start: node.selectionStart, end: node.selectionEnd };
      }
      const remembered = layoutSelectionRef.current || {};
      const rememberedStart =
        typeof remembered.start === 'number' ? remembered.start : null;
      const rememberedEnd =
        typeof remembered.end === 'number' ? remembered.end : rememberedStart;
      if (
        rememberedStart !== null &&
        rememberedEnd !== null &&
        rememberedStart >= 0 &&
        rememberedEnd >= rememberedStart &&
        rememberedEnd <= current.length
      ) {
        return { start: rememberedStart, end: rememberedEnd };
      }
      return { start: current.length, end: current.length };
    },
    []
  );

  const insertIntoLayout = snippet => {
    const current = layoutText || '';
    let nextValue = '';
    const { start, end } = resolveLayoutSelection(current);
    nextValue = `${current.slice(0, start)}${snippet}${current.slice(end)}`;
    requestAnimationFrame(() => {
      if (!layoutRef.current) return;
      layoutRef.current.focus();
      const cursor = start + snippet.length;
      layoutRef.current.selectionStart = cursor;
      layoutRef.current.selectionEnd = cursor;
      layoutSelectionRef.current = { start: cursor, end: cursor };
    });
    setLayoutText(nextValue);
    const parsed = parseLayoutText(nextValue, draft.sections || []);
    setDraft(prev => ({
      ...prev,
      content: parsed.content,
      sections: parsed.sections,
    }));
  };

  const wrapLayoutSelection = (prefix, suffix, placeholder = 'text') => {
    const current = layoutText || '';
    const { start, end } = resolveLayoutSelection(current);
    const selected = current.slice(start, end) || placeholder;
    const nextValue =
      current.slice(0, start) +
      prefix +
      selected +
      suffix +
      current.slice(end);
    setLayoutText(nextValue);
    const parsed = parseLayoutText(nextValue, draft.sections || []);
    setDraft(prev => ({
      ...prev,
      content: parsed.content,
      sections: parsed.sections,
    }));
    requestAnimationFrame(() => {
      if (!layoutRef.current) return;
      layoutRef.current.focus();
      const selectionStart = start + prefix.length;
      const selectionEnd = selectionStart + selected.length;
      layoutRef.current.selectionStart = selectionStart;
      layoutRef.current.selectionEnd = selectionEnd;
      layoutSelectionRef.current = { start: selectionStart, end: selectionEnd };
    });
  };

  const insertBulletedList = () => {
    const current = layoutText || '';
    const { start, end } = resolveLayoutSelection(current);
    const selected = current.slice(start, end);
    if (!selected.trim()) {
      insertIntoLayout('- Item one\n- Item two');
      return;
    }
    const lines = selected.split(/\r?\n/).map(line => {
      const trimmed = line.trim();
      return trimmed.startsWith('- ') ? trimmed : `- ${trimmed || 'Item'}`;
    });
    const nextValue =
      current.slice(0, start) + lines.join('\n') + current.slice(end);
    setLayoutText(nextValue);
    const parsed = parseLayoutText(nextValue, draft.sections || []);
    setDraft(prev => ({
      ...prev,
      content: parsed.content,
      sections: parsed.sections,
    }));
    requestAnimationFrame(() => {
      if (!layoutRef.current) return;
      layoutRef.current.focus();
      const cursor = start + lines.join('\n').length;
      layoutRef.current.selectionStart = cursor;
      layoutRef.current.selectionEnd = cursor;
      layoutSelectionRef.current = { start: cursor, end: cursor };
    });
  };

  const normalizeLanguagesForSave = () => {
    const entries = Array.isArray(draft.languages) ? draft.languages : [];
    return entries
      .map(entry => {
        const code = typeof entry.code === 'string' ? entry.code.trim() : '';
        if (!code) return null;
        const label =
          typeof entry.label === 'string' && entry.label.trim()
            ? entry.label.trim()
            : code;
        const content =
          typeof entry.content === 'string' ? entry.content : '';
        const parsed = parseLayoutText(content, draft.sections || []);
        return {
          code,
          label,
          content,
          sections: Array.isArray(parsed.sections) ? parsed.sections : [],
        };
      })
      .filter(Boolean);
  };

  const saveTemplate = async () => {
    if (!draft.name.trim()) {
      showNotification?.('Template name is required', 'error');
      return;
    }
    const saveScope = draft.saveScope === 'global' ? 'global' : 'event';
    const targetGroupId = Number(
      (isAdmin || canSelectTargetGroup
        ? draft.targetGroupId
        : visibleGroups[0]?.id || primaryGroupId) || 0
    );
    if (!targetGroupId) {
      showNotification?.('Choose a target group for this template.', 'error');
      return;
    }
    if (saveScope === 'event' && !selectedEventId) {
      showNotification?.('Choose an event scope before saving.', 'error');
      return;
    }
    if (!isAdmin && !userGroupIdSet.has(targetGroupId)) {
      showNotification?.('You can only save templates to your assigned groups.', 'error');
      return;
    }
    try {
      const payload = {
        ...draft,
        scope: saveScope,
        visibility: `group-${targetGroupId}`,
        event_id: saveScope === 'event' ? selectedEventId : null,
        languages: normalizeLanguagesForSave(),
        sections: normalizeTextTemplateSections(draft.sections),
      };
      if (editingId) {
        const updated = await api.updateTextTemplate(editingId, payload);
        setTemplates(prev =>
          prev.map(t => (t.id === editingId ? updated : t))
        );
        showNotification?.('Template updated', 'success');
      } else {
        const created = await api.createTextTemplate(payload);
        setTemplates(prev => [created, ...prev]);
        showNotification?.('Template created', 'success');
      }
      resetDraft();
    } catch (err) {
      showNotification?.(err.message || 'Failed to save template', 'error');
    }
  };

  const deleteTemplate = async id => {
    if (!id) return;
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.deleteTextTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      setSelectedTemplateIds(prev => prev.filter(tid => tid !== id));
      showNotification?.('Template deleted', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Failed to delete', 'error');
    }
  };

  const duplicateTemplate = async tpl => {
    if (!tpl?.id) return;
    try {
      const created = await api.duplicateTextTemplate(tpl.id);
      setTemplates(prev => [created, ...prev]);
      showNotification?.('Template duplicated', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Failed to duplicate template', 'error');
    }
  };

  const handleToggleHideEvent = useCallback(() => {
    if (!selectedEventId) {
      showNotification?.('Select an event first.', 'error');
      return;
    }
    const eventId = Number(selectedEventId);
    if (!Number.isFinite(eventId)) return;
    const isHidden = hiddenEventIdSet.has(eventId);
    const next = isHidden
      ? hiddenEventIds.filter(id => Number(id) !== eventId)
      : [...hiddenEventIds, eventId];
    setHiddenEventIds(next);
    writeHiddenEvents(user?.id, next);
    showNotification?.(
      isHidden ? 'Event unhidden.' : 'Event hidden from your view.',
      'success'
    );
  }, [hiddenEventIdSet, hiddenEventIds, selectedEventId, showNotification, user?.id]);

  const startEdit = tpl => {
    const visibilityGroupId = parseVisibilityGroupId(tpl.visibility);
    const nextScope = tpl.event_id ? 'event' : 'global';
    setEditingId(tpl.id);
    setDraft({
      name: tpl.name,
      role: tpl.role,
      visibility: tpl.visibility || '',
      saveScope: nextScope,
      targetGroupId:
        visibilityGroupId ||
        (isAdmin ? variableGroupId : visibleGroups[0]?.id || primaryGroupId),
      content: tpl.content || '',
      languages: Array.isArray(tpl.languages) ? tpl.languages : [],
      sections: Array.isArray(tpl.sections) ? tpl.sections : [],
    });
    if (tpl.event_id) {
      setSelectedEventId(Number(tpl.event_id));
    }
  };

  const addVariable = async payload => {
    const targetGroupId = Number((isAdmin ? variableGroupId : primaryGroupId) || 0);
    if (!targetGroupId) {
      showNotification?.('Your account has no group assigned yet.', 'error');
      return;
    }
    try {
      const created = await api.createEventVariable(
        { scope: 'group', groupId: targetGroupId },
        payload
      );
      const targetGroupName =
        visibleGroups.find(group => Number(group.id) === targetGroupId)?.name ||
        created?.Group?.name ||
        `Group ${targetGroupId}`;
      setVariables(prev => [
        ...prev,
        { ...created, sourceGroupName: targetGroupName },
      ]);
      showNotification?.('Variable added', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Failed to add variable', 'error');
    }
  };

  const updateVariable = async (id, payload) => {
    try {
      const updated = await api.updateEventVariable(id, payload);
      setVariables(prev =>
        prev.map(v =>
          v.id === id
            ? {
                ...updated,
                sourceGroupName:
                  v.sourceGroupName ||
                  updated?.Group?.name ||
                  `Group ${updated?.group_id}`,
              }
            : v
        )
      );
      showNotification?.('Variable updated', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Failed to update variable', 'error');
    }
  };

  const addLanguageVersion = () => {
    const existingCodes = new Set(
      (draft.languages || []).map(lang => (lang?.code || '').toLowerCase())
    );
    const suggestion = (translateLanguages || []).find(
      lang => lang?.id && !existingCodes.has(String(lang.id).toLowerCase())
    );
    const next = {
      code: suggestion?.id || '',
      label: suggestion?.label || '',
      content: '',
    };
    setDraft(prev => ({
      ...prev,
      languages: [...(prev.languages || []), next],
    }));
  };

  const updateLanguage = (index, field, value) => {
    setDraft(prev => {
      const languages = Array.isArray(prev.languages) ? [...prev.languages] : [];
      languages[index] = { ...(languages[index] || {}), [field]: value };
      return { ...prev, languages };
    });
  };

  const applyLanguagePreset = (index, langId) => {
    const match = (translateLanguages || []).find(
      lang => String(lang.id) === String(langId)
    );
    if (!match) return;
    updateLanguage(index, 'code', match.id || '');
    updateLanguage(index, 'label', match.label || match.id || '');
  };

  const removeLanguage = index => {
    setDraft(prev => {
      const languages = Array.isArray(prev.languages) ? [...prev.languages] : [];
      languages.splice(index, 1);
      return { ...prev, languages };
    });
  };

  const copyMainContentToLanguage = index => {
    const fullLayout = layoutText || buildLayoutText(draft);
    updateLanguage(index, 'content', fullLayout || '');
  };

  const pasteClipboardToLanguage = async index => {
    if (!navigator?.clipboard?.readText) {
      showNotification?.('Clipboard access is not available.', 'error');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) {
        showNotification?.('Clipboard is empty.', 'error');
        return;
      }
      updateLanguage(index, 'content', text);
    } catch (err) {
      showNotification?.(err.message || 'Failed to read clipboard.', 'error');
    }
  };

  const deleteVariable = async id => {
    if (!window.confirm('Delete this variable?')) return;
    try {
      await api.deleteEventVariable(id);
      setVariables(prev => prev.filter(v => v.id !== id));
      showNotification?.('Variable deleted', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Failed to delete variable', 'error');
    }
  };

  const runAiRequest = async ({
    command = 'improve',
    language = '',
    languageLabel = '',
    customPrompt = '',
    selection = '',
  } = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };
    const languagePrompt =
      command === 'translate' && (languageLabel || language)
        ? `Translate this text into ${languageLabel || language}.`
        : '';
    const prompt =
      (customPrompt || '').trim() ||
      languagePrompt.trim() ||
      'Polish this copy for clarity and tone.';

    const response = await fetch(apiUrl('ai/generate'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        command,
        selection,
        prompt,
        language: command === 'translate' ? language : '',
        scope: 'selection',
        targetSection: 'text-template-library',
      }),
    });
    const retryAfterHeader = response.headers.get('retry-after');
    const retryAfterSeconds = Number.parseInt(retryAfterHeader || '', 10);
    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    if (!response.ok) {
      if (response.status === 429) {
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
          throw new Error(
            `AI rate limit reached. Try again in about ${retryAfterSeconds} seconds.`
          );
        }
        throw new Error('AI rate limit reached. Please wait a moment and try again.');
      }
      throw new Error(data.error || 'AI request failed');
    }
    const improved = (data.result || '').trim();
    if (!improved) {
      throw new Error('AI response was empty.');
    }
    return improved;
  };

  const addAiResponse = (text, meta = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const entry = {
      id,
      text,
      createdAt: new Date().toISOString(),
      meta,
    };
    setAiResponses(prev => [entry, ...prev].slice(0, 10));
    setActiveAiResponseId(id);
    setAiModalOpen(true);
  };

  const runAiAction = async ({
    command = 'improve',
    language = '',
    languageLabel = '',
    customPrompt = '',
    actionLabel = '',
  } = {}) => {
    const content = (layoutText || buildLayoutText(draft) || '').trim();
    if (!content.trim()) {
      showNotification?.('Add some base content before using AI.', 'error');
      return;
    }

    setAiBusy(true);
    try {
      const improved = await runAiRequest({
        command,
        language,
        languageLabel,
        customPrompt,
        selection: content,
      });
      setLastAiAction({
        command,
        label: actionLabel || languageLabel || language || command,
        language,
        languageLabel,
        customPrompt,
      });
      addAiResponse(improved, {
        command,
        label: actionLabel || languageLabel || language || command,
      });
      showNotification?.('AI response ready.', 'success');
    } catch (err) {
      showNotification?.(err.message || 'AI request failed', 'error');
    } finally {
      setAiBusy(false);
    }
  };

  const handleAiQuickAction = async ({ command, label, language, prompt }) => {
    setAiMenuOpen(false);
    await runAiAction({
      command,
      language,
      languageLabel: label,
      customPrompt: prompt,
      actionLabel: label,
    });
  };

  const getAiSelection = () => {
    const node = aiResponseRef.current;
    if (!node || typeof node.selectionStart !== 'number') return '';
    const start = node.selectionStart;
    const end = node.selectionEnd;
    if (end <= start) return '';
    return node.value.slice(start, end);
  };

  const handleCopyAiSelection = async () => {
    const selection = getAiSelection() || activeAiResponse?.text || '';
    if (!selection.trim()) {
      showNotification?.('No AI text to copy.', 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(selection);
      showNotification?.('Copied to clipboard.', 'success');
    } catch {
      showNotification?.('Copy failed.', 'error');
    }
  };

  const handleRewriteSelection = async () => {
    const selection = getAiSelection();
    if (!selection.trim()) {
      showNotification?.('Select some AI text to rewrite.', 'error');
      return;
    }
    setAiModalBusy(true);
    try {
      const improved = await runAiRequest({
        command: lastAiAction.command || 'improve',
        language: lastAiAction.language || '',
        languageLabel: lastAiAction.languageLabel || '',
        customPrompt: lastAiAction.customPrompt || '',
        selection,
      });
      addAiResponse(improved, {
        command: lastAiAction.command || 'improve',
        label: lastAiAction.label || 'Improve',
        source: 'selection',
      });
      showNotification?.('AI rewrite ready.', 'success');
    } catch (err) {
      showNotification?.(err.message || 'AI request failed', 'error');
    } finally {
      setAiModalBusy(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    const groupFiltered = (templates || []).filter(tpl => {
      if (activeLibraryGroup === 'all') {
        return true;
      }
      const targetId = Number(activeLibraryGroup);
      if (!Number.isFinite(targetId)) return true;
      const gid = parseVisibilityGroupId(tpl?.visibility);
      return gid === targetId;
    });

    const query = templateFilter.trim().toLowerCase();
    const searchFiltered = query
      ? groupFiltered.filter(tpl => {
          const fields = [
            tpl?.name,
            tpl?.content,
            tpl?.visibility,
            tpl?.role,
            tpl?.ownerUsername,
          ];
          return fields
            .filter(Boolean)
            .map(value => String(value).toLowerCase())
            .some(value => value.includes(query));
        })
      : groupFiltered;

    const favorites = new Set(favoriteTemplateIds.map(id => Number(id)));
    const withIndex = searchFiltered.map((tpl, index) => ({ tpl, index }));
    withIndex.sort((a, b) => {
      const aFav = favorites.has(Number(a.tpl?.id));
      const bFav = favorites.has(Number(b.tpl?.id));
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return a.index - b.index;
    });
    return withIndex.map(item => item.tpl);
  }, [templates, favoriteTemplateIds, activeLibraryGroup, templateFilter]);

  const toggleSelected = id => {
    setSelectedTemplateIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectedTemplates = useMemo(
    () => templates.filter(t => selectedTemplateIds.includes(t.id)),
    [templates, selectedTemplateIds]
  );

  const getTemplateLanguageOptions = useCallback(template => {
    const entries = Array.isArray(template?.languages) ? template.languages : [];
    const seen = new Set();
    return entries
      .map(entry => {
        const code = String(entry?.code || '').trim();
        if (!code || seen.has(code.toLowerCase())) return null;
        seen.add(code.toLowerCase());
        return {
          code,
          label: entry?.label ? String(entry.label) : code.toUpperCase(),
        };
      })
      .filter(Boolean);
  }, []);

  useEffect(() => {
    if (!selectedTemplates.length) {
      setBatchLanguageSelections({});
      return;
    }
    setBatchLanguageSelections(prev => {
      const next = {};
      selectedTemplates.forEach(template => {
        const idKey = String(template.id);
        const options = getTemplateLanguageOptions(template);
        if (!options.length) return;
        const allowedCodes = new Set(options.map(item => item.code));
        const existing = Array.isArray(prev[idKey]) ? prev[idKey] : [];
        const filtered = existing.filter(code => allowedCodes.has(code));
        next[idKey] = filtered.length
          ? filtered
          : options.map(item => item.code);
      });
      return next;
    });
  }, [selectedTemplates, getTemplateLanguageOptions]);

  const usedVariableKeys = useMemo(() => {
    const keys = new Set();
    const extract = text => {
      if (!text) return;
      const regex = /{{\s*([\w.-]+)\s*}}/g;
      let match = regex.exec(text);
      while (match) {
        keys.add(match[1]);
        match = regex.exec(text);
      }
    };
    selectedTemplates.forEach(template => {
      extract(template.content);
      (template.languages || []).forEach(lang => extract(lang?.content));
      (template.sections || []).forEach(section => {
        extract(section?.title);
        extract(section?.body);
        extract(section?.ctaText);
        extract(section?.ctaUrl);
        extract(section?.ctaTextSecondary);
        extract(section?.ctaUrlSecondary);
      });
    });
    return Array.from(keys);
  }, [selectedTemplates]);

  const usedVariables = useMemo(() => {
    return usedVariableKeys
      .map(key => variables.find(v => v.key === key) || { key, label: key })
      .sort((a, b) =>
        (a.label || a.key || '').localeCompare(b.label || b.key || '')
      );
  }, [usedVariableKeys, variables]);

  const handleBatchGenerate = async () => {
    try {
      if (!selectedTemplates.length) {
        showNotification?.('Select at least one template to generate', 'error');
        return;
      }
      const invalidTemplate = selectedTemplates.find(template => {
        const options = getTemplateLanguageOptions(template);
        if (!options.length) return false;
        const chosen = batchLanguageSelections[String(template.id)] || [];
        return !Array.isArray(chosen) || chosen.length === 0;
      });
      if (invalidTemplate) {
        showNotification?.(
          `Choose at least one language for "${invalidTemplate.name}".`,
          'error'
        );
        return;
      }
      const languageSelections = selectedTemplates.reduce((acc, template) => {
        const options = getTemplateLanguageOptions(template);
        if (!options.length) return acc;
        acc[String(template.id)] = batchLanguageSelections[String(template.id)] || [];
        return acc;
      }, {});
      const payload = {
        event_id: selectedEventId,
        templates: selectedTemplates,
        values: batchValues,
        languages: languageSelections,
      };
      const { results } = await api.batchGenerateTextTemplates(payload);
      setGenerated(results || []);
      showNotification?.('Templates generated', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Batch generation failed', 'error');
    }
  };

  const insertVariable = key => {
    insertIntoLayout(`{{${key}}}`);
  };

  const handleVariableLabelChange = useCallback(event => {
    const label = event.target.value;
    setDraft(prev => {
      const currentKey = String(prev?.varKey || '').trim();
      const previousLabel = String(prev?.varLabel || '');
      const previousDerivedKey = normalizeVariableKey(previousLabel);
      const nextDerivedKey = normalizeVariableKey(label);
      const keyWasManual = Boolean(prev?.varKeyTouched);
      const shouldAutofillKey =
        !keyWasManual ||
        !currentKey ||
        currentKey === previousDerivedKey;

      return {
        ...prev,
        varLabel: label,
        varKey: shouldAutofillKey ? nextDerivedKey : prev.varKey,
      };
    });
  }, []);

  const handleVariableKeyChange = useCallback(event => {
    const nextKey = normalizeVariableKey(event.target.value);
    setDraft(prev => ({
      ...prev,
      varKey: nextKey,
      varKeyTouched: true,
    }));
  }, []);

  const toggleTemplateFavorite = templateId => {
    const normalizedId = Number(templateId);
    if (!Number.isFinite(normalizedId)) return;
    setFavoriteTemplateIds(prev => {
      const next = prev.includes(normalizedId)
        ? prev.filter(id => id !== normalizedId)
        : [...prev, normalizedId];
      if (favoritesUserKey) {
        const parsed = readFavoritesByUser();
        parsed[favoritesUserKey] = next;
        writeFavoritesByUser(parsed);
      }
      return next;
    });
  };

  const toggleBatchLanguage = (templateId, languageCode) => {
    const idKey = String(templateId);
    setBatchLanguageSelections(prev => {
      const current = Array.isArray(prev[idKey]) ? prev[idKey] : [];
      const exists = current.includes(languageCode);
      return {
        ...prev,
        [idKey]: exists
          ? current.filter(code => code !== languageCode)
          : [...current, languageCode],
      };
    });
  };

  const setAllBatchLanguages = (templateId, languageCodes) => {
    const idKey = String(templateId);
    setBatchLanguageSelections(prev => ({
      ...prev,
      [idKey]: [...languageCodes],
    }));
  };

  const renameTemplate = async tpl => {
    const newName = window.prompt('Rename template', tpl.name);
    if (!newName || !newName.trim() || newName.trim() === tpl.name) return;
    try {
      const updated = await api.updateTextTemplate(tpl.id, { name: newName.trim() });
      setTemplates(prev => prev.map(t => (t.id === tpl.id ? updated : t)));
      showNotification?.('Template renamed', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Failed to rename template', 'error');
    }
  };

  const openSaveDialog = () => {
    if (!targetEventForSave) {
      showNotification?.('Choose an event to save generated templates.', 'error');
      return;
    }
    if (!generated.length) {
      showNotification?.('Generate templates first.', 'error');
      return;
    }
    setSaveSummary(null);
    setSaveDialogOpen(true);
  };

  const closeSaveDialog = () => {
    if (savingGenerated) return;
    setSaveDialogOpen(false);
  };

  const resolveVisibility = visibility => {
    if (visibility === 'global') return isAdmin ? 'global' : 'user';
    if (typeof visibility === 'string' && visibility.startsWith('group-')) {
      const gid = Number(visibility.slice(6));
      if (isAdmin || userGroupIdSet.has(gid)) return visibility;
      return 'user';
    }
    return 'user';
  };

  const buildDerivedTemplateName = (baseName, templateName, language) => {
    const safeBase = baseName || 'Base design';
    const safeTemplate = templateName || 'Template';
    const languageSuffix = language ? ` (${String(language).toUpperCase()})` : '';
    return `${safeBase} — ${safeTemplate}${languageSuffix}`;
  };

  const saveGeneratedDesigns = async () => {
    if (!selectedDesignId) {
      showNotification?.('Choose a base design template.', 'error');
      return;
    }
    if (!targetEventForSave) {
      showNotification?.('Choose an event to save generated templates.', 'error');
      return;
    }
    if (!generated.length) {
      showNotification?.('Generate templates first.', 'error');
      return;
    }

    const baseTemplate = designTemplates.find(t => t.id === selectedDesignId);
    if (!baseTemplate) {
      showNotification?.('Base design template not found.', 'error');
      return;
    }
    if (!isBaseDesignTemplate(baseTemplate)) {
      showNotification?.('Selected template is not marked as base design.', 'error');
      return;
    }
    if (!isAdmin && Number(baseTemplate.event_id) !== Number(targetEventForSave)) {
      showNotification?.(
        'Selected base design does not belong to the chosen event.',
        'error'
      );
      return;
    }

    const baseSettings = mapTemplateToConfigurator(baseTemplate);
    const defaults = deriveDefaultsFromGlobals(globalSettings || null);
    const baseUsesLegacySalutation =
      baseSettings.salutationInput === LEGACY_SALUTATION_DEFAULT;
    const baseUsesLegacyClosing =
      baseSettings.closingText === LEGACY_CLOSING_DEFAULT;
    const baseUsesLegacySalutationTranslations = isExactLegacyTranslationMap(
      baseSettings.salutationTranslations,
      LEGACY_SALUTATION_TRANSLATIONS
    );
    const baseUsesLegacyClosingTranslations = isExactLegacyTranslationMap(
      baseSettings.closingTextTranslations,
      LEGACY_CLOSING_TRANSLATIONS
    );
    const mergeTranslations = (primary, secondary) => ({
      ...(secondary && typeof secondary === 'object' ? secondary : {}),
      ...(primary && typeof primary === 'object' ? primary : {}),
    });
    const normalizedBaseSettings = {
      ...defaults,
      ...baseSettings,
      salutationTranslations: mergeTranslations(
        baseUsesLegacySalutationTranslations
          ? defaults.salutationTranslations
          : baseSettings.salutationTranslations,
        defaults.salutationTranslations
      ),
      closingTextTranslations: mergeTranslations(
        baseUsesLegacyClosingTranslations
          ? defaults.closingTextTranslations
          : baseSettings.closingTextTranslations,
        defaults.closingTextTranslations
      ),
      footerTextTranslations: mergeTranslations(
        baseSettings.footerTextTranslations,
        defaults.footerTextTranslations
      ),
      salutationInput:
        baseUsesLegacySalutation
          ? defaults.salutationInput
          : baseSettings.salutationInput ?? defaults.salutationInput,
      closingText: baseUsesLegacyClosing
        ? defaults.closingText
        : baseSettings.closingText ?? defaults.closingText,
      footerText:
        !baseSettings.footerText ||
        baseSettings.footerText === LEGACY_FOOTER_PLACEHOLDER
          ? defaults.footerText
          : baseSettings.footerText,
    };
    const baseVisibility = resolveVisibility(baseTemplate.visibility);
    const results = [];

    setSavingGenerated(true);
    try {
      for (const item of generated) {
        const sourceTemplate =
          templates.find(t => t.id === item.templateId) ||
          selectedTemplates.find(t => t.id === item.templateId) ||
          null;
        const linkedTemplate = {
          id: sourceTemplate?.id || item.templateId,
          name: sourceTemplate?.name || item.name,
          content: item.content,
          sections: item.sections || [],
          updatedAt: sourceTemplate?.updatedAt || sourceTemplate?.updated_at || null,
        };
        const { settings: mergedSettings } = applyTextTemplateToSettings(
          normalizedBaseSettings,
          linkedTemplate,
          { mode: 'auto' }
        );
        // Generated designs must render from current template/settings state.
        // Keeping a base design's visual editor snapshot can override section
        // titles, salutation, and closing with stale HTML.
        delete mergedSettings.visualEditor;
        if (item.language) {
          mergedSettings.textTemplateLanguage = item.language;
        }
        const name = buildDerivedTemplateName(
          baseTemplate.name,
          linkedTemplate.name || item.name,
          item.language
        );
        const payload = buildTemplatePayload(mergedSettings, {
          includeId: false,
          overrides: {
            name,
            visibility: baseVisibility,
            event_id: targetEventForSave,
            baseDesign: false,
          },
        });
        // eslint-disable-next-line no-await-in-loop
        const result = await api.saveTemplate(user?.id, payload);
        results.push({ name, success: Boolean(result?.success ?? true) });
      }

      setSaveSummary({
        total: results.length,
        successCount: results.filter(r => r.success).length,
        results,
      });
      showNotification?.('Generated designs saved.', 'success');
    } catch (err) {
      showNotification?.(err.message || 'Failed to save generated designs', 'error');
    } finally {
      setSavingGenerated(false);
    }
  };

  const selectedCount = selectedTemplateIds.length;
  const filteredCount = filteredTemplates.length;
  const hasGroup = isAdmin || userGroupIds.length > 0;
  const targetEventName =
    events.find(ev => Number(ev.id) === Number(targetEventForSave))?.name ||
    'selected event';
  const canEditTemplate = tpl => {
    if (!tpl) return false;
    if (isAdmin) return true;
    if (typeof tpl.can_edit === 'boolean') return tpl.can_edit;
    if (tpl.visibility === 'user') {
      return Number(tpl.owner_id) === Number(user?.id);
    }
    if (typeof tpl.visibility === 'string' && tpl.visibility.startsWith('group-')) {
      const gid = Number(tpl.visibility.slice(6));
      return effectiveGroupIdSet.has(gid);
    }
    return false;
  };
  const canEditVariable = variable => {
    if (isAdmin) return true;
    return effectiveGroupIdSet.has(Number(variable?.group_id));
  };
  const describeTemplateVisibility = visibility => {
    if (!visibility) return 'My template';
    if (visibility === 'global') return 'Global';
    if (visibility === 'user') return 'My template';
    if (typeof visibility === 'string' && visibility.startsWith('group-')) {
      const gid = Number(visibility.replace('group-', ''));
      const group =
        (accessibleGroups || []).find(item => Number(item?.id) === gid) ||
        (visibleGroups || []).find(item => Number(item?.id) === gid);
      return group?.name || `Group ${gid}`;
    }
    return visibility;
  };

  const handleGoToEventTemplateOverview = () => {
    if (!targetEventForSave) return;
    writeSelectedEventScope(user?.id, String(targetEventForSave));
    setSaveDialogOpen(false);
    onNavigate?.('templateSelect');
  };

  return (
    <div className="space-y-8">
      <FormGroup title="Event setup">
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Choose event scope
            </label>
            <select
              value={selectedEventId || ''}
              onChange={e => setSelectedEventId(Number(e.target.value) || null)}
              className="form-select w-full"
            >
              <option value="">Select event</option>
              {visibleSelectableEvents.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                  {ev.status === 'archived' ? ' (Archived)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              Required when template save scope is set to "Selected event".
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowArchivedEvents(prev => !prev)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  showArchivedEvents
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-slate-200 bg-white text-slate-500 hover:text-primary hover:border-primary/40'
                }`}
              >
                <span className="text-[10px] uppercase tracking-[0.2em]">Archived</span>
                <span>{showArchivedEvents ? 'On' : 'Off'}</span>
              </button>
              <button
                type="button"
                onClick={() => setShowHiddenEvents(prev => !prev)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  showHiddenEvents
                    ? 'border-amber-300 bg-amber-50 text-amber-700'
                    : 'border-slate-200 bg-white text-slate-500 hover:text-amber-700 hover:border-amber-300'
                }`}
              >
                <span className="text-[10px] uppercase tracking-[0.2em]">Hidden</span>
                <span>{showHiddenEvents ? 'On' : 'Off'}</span>
              </button>
              <button
                type="button"
                onClick={handleToggleHideEvent}
                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/40 hover:text-primary disabled:opacity-50"
                disabled={!selectedEventId}
              >
                {hiddenEventIdSet.has(Number(selectedEventId))
                  ? 'Unhide event'
                  : 'Hide event'}
              </button>
            </div>
          </div>
        </div>
      </FormGroup>

      <FormGroup title="Templates">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="relative space-y-4 rounded-2xl border border-slate-200 bg-white/90 p-5 pb-16 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {editingId ? 'Editing template' : 'Create a template'}
                </p>
                <p className="text-xs text-slate-500">
                  Name it and add your message.
                </p>
              </div>
              {editingId && (
                <button
                  type="button"
                  onClick={resetDraft}
                  className="text-xs font-semibold text-slate-500 transition hover:text-slate-900"
                >
                  Cancel edit
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <FormInput
                label="Template name"
                value={draft.name}
                onChange={e => setDraft(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Confirmation"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Save scope
                </label>
                <select
                  value={draft.saveScope || 'event'}
                  onChange={e =>
                    setDraft(prev => ({ ...prev, saveScope: e.target.value }))
                  }
                  className="form-select w-full"
                >
                  <option value="event">Selected event</option>
                  <option value="global">Global pool</option>
                </select>
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target group
                  </label>
                  <select
                    value={draft.targetGroupId || ''}
                    onChange={e =>
                      setDraft(prev => ({
                        ...prev,
                        targetGroupId: Number(e.target.value) || null,
                      }))
                    }
                    className="form-select w-full"
                    disabled={!canSelectTargetGroup}
                  >
                    {canSelectTargetGroup && <option value="">Select group</option>}
                    {(visibleGroups || []).map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content layout
              </label>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Formatting
                </span>
                <button
                  type="button"
                  onMouseDown={preserveLayoutSelectionOnMouseDown}
                  onClick={() => wrapLayoutSelection('**', '**', 'bold text')}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                >
                  <span className="font-bold">B</span>
                </button>
                <button
                  type="button"
                  onMouseDown={preserveLayoutSelectionOnMouseDown}
                  onClick={() => wrapLayoutSelection('*', '*', 'italic text')}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                >
                  <span className="italic">I</span>
                </button>
                <button
                  type="button"
                  onMouseDown={preserveLayoutSelectionOnMouseDown}
                  onClick={() => wrapLayoutSelection('__', '__', 'underlined text')}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                >
                  <span className="underline">U</span>
                </button>
                <button
                  type="button"
                  onMouseDown={preserveLayoutSelectionOnMouseDown}
                  onClick={insertBulletedList}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                >
                  • List
                </button>
                <button
                  type="button"
                  onMouseDown={preserveLayoutSelectionOnMouseDown}
                  onClick={() =>
                    wrapLayoutSelection('[', '](https://example.com)', 'Link text')
                  }
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                >
                  Link
                </button>
                <button
                  type="button"
                  onMouseDown={preserveLayoutSelectionOnMouseDown}
                  onClick={() =>
                    insertIntoLayout(
                      '| Column A | Column B |\\n| --- | --- |\\n| Value 1 | Value 2 |'
                    )
                  }
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                >
                  Table
                </button>
              </div>
              <textarea
                ref={layoutRef}
                rows="10"
                value={layoutText}
                onChange={handleLayoutChange}
                onSelect={event => rememberLayoutSelection(event.target)}
                onClick={event => rememberLayoutSelection(event.target)}
                onKeyUp={event => rememberLayoutSelection(event.target)}
                onFocus={event => rememberLayoutSelection(event.target)}
                onBlur={event => {
                  rememberLayoutSelection(event.target);
                  normalizeLayoutText();
                }}
                className="form-textarea w-full"
                placeholder="Write your intro, then add [[SECTION: Title]] and [[CTA: text:buttontext, url:changeme]] markers."
              />
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                  Insert a variable
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {variables.length === 0 && (
                    <span className="text-xs text-slate-400">
                      No variables yet.
                    </span>
                  )}
                  {variables.map(variable => (
                    <button
                      key={variable.id}
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() => insertVariable(variable.key)}
                      className="pill-tag text-[11px] uppercase tracking-[0.2em] hover:border-primary hover:text-primary"
                    >
                      {variable.label}
                    </button>
                  ))}
                </div>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Quick inserts
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                      Editable
                      <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500">
                      Fixed syntax
                      <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() => insertIntoLayout('[[SECTION: New section]]\n')}
                      className={QUICK_INSERT_CARD_CLASS}
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Section title
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-slate-700">
                        [[SECTION:
                        <span className="mx-1 rounded bg-emerald-100 px-1 text-emerald-700">
                          Your title
                        </span>
                        ]]
                      </span>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        Edit the green text only.
                      </span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() =>
                        insertIntoLayout('[[CTA: text:buttontext, url:changeme]]\n')
                      }
                      className="group rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-primary hover:text-primary"
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        CTA button
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-slate-700">
                        [[CTA: text:
                        <span className="mx-1 rounded bg-emerald-100 px-1 text-emerald-700">
                          Button text
                        </span>
                        , url:
                        <span className="mx-1 rounded bg-emerald-100 px-1 text-emerald-700">
                          https://...
                        </span>
                        ]]
                      </span>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        Keep the gray keywords.
                      </span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() =>
                        insertIntoLayout(
                          '[[CTA: text:Pay Now, url:https://kit-react.de/portal/react/hotel/initPayment?codev2h=$' +
                            '{p.vigenere2CodeHex}&mnemonic={{mnemonicCode}}]]\n'
                        )
                      }
                      className="group rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-primary hover:text-primary"
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Pay now button
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-slate-700 break-all">
                        {'[[CTA: text:Pay Now, url:https://kit-react.de/portal/react/hotel/initPayment?codev2h=$' +
                          '{p.vigenere2CodeHex}&mnemonic={{mnemonicCode}}]]'}
                      </span>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        Inserts a preconfigured payment CTA marker.
                      </span>
                    </button>
                    {QUICK_LINK_BUTTONS.map(button => (
                      <button
                        key={button.title}
                        type="button"
                        onMouseDown={preserveLayoutSelectionOnMouseDown}
                        onClick={() => insertIntoLayout(button.snippet)}
                        className={QUICK_INSERT_CARD_CLASS}
                      >
                        <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                          {button.title}
                        </span>
                        <span className="mt-1 block font-mono text-[12px] text-slate-700 break-all">
                          {button.preview}
                        </span>
                        <span className="mt-1 block text-[11px] text-slate-400">
                          {button.helper}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() => insertIntoLayout('[[WALLET]]\n')}
                      className={QUICK_INSERT_CARD_CLASS}
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Wallet block
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-slate-700">
                        [[WALLET]]
                      </span>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        Shows wallet buttons exactly at this marker.
                      </span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() => insertIntoLayout('[[CALENDAR]]\n')}
                      className={QUICK_INSERT_CARD_CLASS}
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Calendar block
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-slate-700">
                        [[CALENDAR]]
                      </span>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        Shows calendar buttons exactly at this marker.
                      </span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() => insertIntoLayout('[[CONGRESS_APP_LINKS]]\n')}
                      className={QUICK_INSERT_CARD_CLASS}
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Congress app links
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-slate-700">
                        [[CONGRESS_APP_LINKS]]
                      </span>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        Shows app store badges exactly at this marker.
                      </span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={preserveLayoutSelectionOnMouseDown}
                      onClick={() =>
                        insertIntoLayout(
                          '<img alt="" src="https://kit-react.de/regasus/api/rest/v1/qrcode?content=$' +
                            '{p.barcode}" height="120" width="120" style="width:138px;height:138px;" />\n'
                        )
                      }
                      className="group rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-primary hover:text-primary"
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Participant QR code
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-slate-700 break-all">
                        https://kit-react.de/regasus/api/rest/v1/qrcode?content=${'{'}p.barcode{'}'}
                      </span>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        Inserts a ready-to-use QR image block.
                      </span>
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Section/CTA styling comes from the base design template. Wallet, Calendar and Congress App links are only rendered when their marker is present.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/80 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Language versions (optional)
                  </p>
                  <p className="text-xs text-slate-500">
                    Save translated versions in the same template.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addLanguageVersion}
                  className="text-xs font-semibold text-primary hover:text-primary/90"
                >
                  + Add language
                </button>
              </div>

              {(draft.languages || []).length === 0 && (
                <p className="mt-3 text-xs text-slate-500">
                  No additional languages yet.
                </p>
              )}

              <div className="mt-3 space-y-3">
                {(draft.languages || []).map((lang, index) => (
                  <div
                    key={`${lang.code || 'lang'}-${index}`}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-3"
                  >
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto]">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Language
                        </label>
                        <select
                          value={lang.code || ''}
                          onChange={e => applyLanguagePreset(index, e.target.value)}
                          className="form-select w-full"
                        >
                          <option value="">Choose language</option>
                          {translateLanguages.map(item => (
                            <option key={item.id || item.label} value={item.id || item.label}>
                              {item.label || item.id}
                            </option>
                          ))}
                        </select>
                      </div>
                      <FormInput
                        label="Language code"
                        value={lang.code || ''}
                        onChange={e => updateLanguage(index, 'code', e.target.value)}
                        placeholder="e.g. de"
                      />
                      <button
                        type="button"
                        onClick={() => removeLanguage(index)}
                        className="text-xs font-semibold text-rose-500 hover:text-rose-600 self-end"
                      >
                        Remove
                      </button>
                    </div>
                    <FormInput
                      label="Label"
                      value={lang.label || ''}
                      onChange={e => updateLanguage(index, 'label', e.target.value)}
                      placeholder="e.g. Deutsch"
                    />
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Content
                      </label>
                      <textarea
                        rows="4"
                        value={lang.content || ''}
                        onChange={e => updateLanguage(index, 'content', e.target.value)}
                        className="form-textarea w-full"
                        placeholder="Translated content"
                      />
                    </div>
                    <div className="flex flex-wrap justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => pasteClipboardToLanguage(index)}
                        className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                      >
                        Paste clipboard
                      </button>
                      <button
                        type="button"
                        onClick={() => copyMainContentToLanguage(index)}
                        className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                      >
                        Use main content
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                className="nav-button-primary w-full sm:flex-1"
                onClick={saveTemplate}
              >
                {editingId ? 'Update template' : 'Save template'}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="nav-button-secondary w-full sm:w-auto"
                  onClick={resetDraft}
                >
                  Cancel edit
                </button>
              )}
              <div className="relative sm:flex-none" aria-live="polite">
                <button
                  ref={aiTriggerRef}
                  type="button"
                  onClick={() => setAiMenuOpen(prev => !prev)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold shadow-lg transition ${
                    aiBusy
                      ? 'border-gray-300 bg-gray-100 cursor-wait text-gray-500'
                      : 'border-primary bg-primary text-white hover:shadow-xl'
                  }`}
                  aria-expanded={aiMenuOpen}
                  aria-controls="ai-action-menu"
                  title="AI Assist"
                >
                  <span>AI Assist</span>
                </button>
                {aiResponses.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setAiModalOpen(true)}
                    className="mt-2 inline-flex w-full items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:border-primary hover:text-primary"
                  >
                    View AI responses ({aiResponses.length})
                  </button>
                )}
                {aiMenuOpen && (
                  <div
                    ref={aiMenuRef}
                    id="ai-action-menu"
                    className="mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-600 shadow-lg"
                  >
                    <div className="space-y-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                          Translate
                        </p>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {translateLanguages.map(lang => (
                            <button
                              key={lang.id || lang.label}
                              type="button"
                              onClick={() =>
                                handleAiQuickAction({
                                  command: 'translate',
                                  label: lang.label || lang.id,
                                  language: lang.id || lang.label,
                                })
                              }
                              className="rounded-xl border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-slate-700 hover:border-primary hover:text-primary"
                            >
                              {lang.label || lang.id}
                            </button>
                          ))}
                        </div>
                      </div>
                      {quickActionGroups.map((group, groupIndex) => (
                        <div key={`${group.label || 'group'}-${groupIndex}`}>
                          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                            {group.label || 'Actions'}
                          </p>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {(group.actions || []).map((action, actionIndex) => (
                              <button
                                key={`${action.label || 'action'}-${actionIndex}`}
                                type="button"
                                onClick={() =>
                                  handleAiQuickAction({
                                    command: action.command || 'improve',
                                    label: action.label || 'AI Action',
                                    prompt: action.prompt || '',
                                  })
                                }
                                className="rounded-xl border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-slate-700 hover:border-primary hover:text-primary"
                              >
                                {action.label || 'Action'}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Library</p>
                <p className="text-xs text-slate-500">
                  {selectedCount} selected · {filteredCount} available
                </p>
              </div>
              {canSwitchLibraryGroup && (
                <div className="min-w-[200px]">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Group
                  </label>
                  <select
                    value={activeLibraryGroup}
                    onChange={e => setActiveLibraryGroup(e.target.value)}
                    className="form-select w-full"
                  >
                    <option value="all">All groups</option>
                    {visibleGroups.map(group => (
                      <option key={group.id} value={String(group.id)}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="search"
                  value={templateFilter}
                  onChange={e => setTemplateFilter(e.target.value)}
                  placeholder="Filter templates..."
                  className="form-input w-52 text-xs"
                />
                <button
                  type="button"
                  className="text-xs px-3 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100"
                  onClick={() => setSelectedTemplateIds(filteredTemplates.map(t => t.id))}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-xs px-3 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100"
                  onClick={() => setSelectedTemplateIds([])}
                >
                  Clear
                </button>
              </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading...</p>}
            {!loading && filteredTemplates.length === 0 && (
              <p className="text-sm text-gray-500">No templates yet.</p>
            )}
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {filteredTemplates.map(tpl => (
                <div
                  key={tpl.id}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedTemplateIds.includes(tpl.id)}
                        onChange={() => toggleSelected(tpl.id)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-semibold text-sm text-gray-800">{tpl.name}</p>
                        {Array.isArray(tpl.languages) && tpl.languages.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {tpl.languages.map((lang, idx) => (
                              <span
                                key={`${lang.code || lang.label || 'lang'}-${idx}`}
                                className="inline-flex items-center rounded-full bg-slate-100 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                              >
                                {lang.label || lang.code}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-gray-500">
                          {describeTemplateVisibility(tpl.visibility)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className={`text-xs px-2 py-1 rounded border ${
                          favoriteTemplateIds.includes(Number(tpl.id))
                            ? 'border-amber-300 bg-amber-50 text-amber-700'
                            : 'border-slate-200 bg-white text-slate-500 hover:text-amber-600'
                        }`}
                        onClick={() => toggleTemplateFavorite(tpl.id)}
                        title={
                          favoriteTemplateIds.includes(Number(tpl.id))
                            ? 'Remove from favorites'
                            : 'Add to favorites'
                        }
                      >
                        {favoriteTemplateIds.includes(Number(tpl.id)) ? '★ Favorite' : '☆ Favorite'}
                      </button>
                      {canEditTemplate(tpl) && (
                        <>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200"
                            onClick={() => startEdit(tpl)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200"
                            onClick={() => renameTemplate(tpl)}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200"
                            onClick={() => duplicateTemplate(tpl)}
                          >
                            Duplicate
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => deleteTemplate(tpl.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-400 line-clamp-2">
                    {tpl.content?.slice(0, 160) || 'Empty template'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </FormGroup>

      <FormGroup title="Team variables">
        <details className="group rounded-2xl border border-slate-200 bg-white/80 p-4">
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Manage shared variables</p>
                <p className="text-xs text-slate-500">
                  {hasGroup
                    ? `${variables.length} available for your team`
                    : 'No group assigned to your account'}
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-500 transition group-open:rotate-180">
                ▾
              </span>
            </div>
          </summary>

          <div className="mt-4 space-y-4">
            {!hasGroup && (
              <p className="text-sm text-gray-500">
                Ask an admin to assign your account to a group to manage variables.
              </p>
            )}
            {hasGroup && (
              <>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group to manage
                    </label>
                    <select
                      value={variableGroupId || ''}
                      onChange={e => setVariableGroupId(Number(e.target.value) || null)}
                      className="form-select w-full"
                    >
                      <option value="">Select group</option>
                      {(visibleGroups || []).map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <FormInput
                    label="Label"
                    value={draft.varLabel || ''}
                    onChange={handleVariableLabelChange}
                    placeholder="e.g., Guest Name"
                  />
                  <FormInput
                    label="Key"
                    value={draft.varKey || ''}
                    onChange={handleVariableKeyChange}
                    placeholder="e.g., guest_name"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <FormInput
                    label="Default value (optional)"
                    value={draft.varDefault || ''}
                    onChange={e => setDraft(prev => ({ ...prev, varDefault: e.target.value }))}
                    placeholder="Optional"
                  />
                  <FormInput
                    label="Help text (optional)"
                    value={draft.varHelp || ''}
                    onChange={e => setDraft(prev => ({ ...prev, varHelp: e.target.value }))}
                    placeholder="Shown to end users"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="nav-button-primary w-full md:w-auto"
                    disabled={
                      !draft.varLabel ||
                      !draft.varKey ||
                      !(isAdmin ? variableGroupId : primaryGroupId)
                    }
                    onClick={() => {
                      const normalizedKey = normalizeVariableKey(draft.varKey);
                      const payload = {
                        label: draft.varLabel,
                        key: normalizedKey,
                        default_value: draft.varDefault,
                        help_text: draft.varHelp,
                      };
                      if (editingVariableId) {
                        updateVariable(editingVariableId, payload);
                      } else {
                        addVariable(payload);
                      }
                      setDraft(prev => ({
                        ...prev,
                        varLabel: '',
                        varKey: '',
                        varKeyTouched: false,
                        varDefault: '',
                        varHelp: '',
                      }));
                      setEditingVariableId(null);
                    }}
                  >
                    {editingVariableId ? 'Update variable' : 'Add variable'}
                  </button>
                  {editingVariableId && (
                    <button
                      type="button"
                      className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                      onClick={() => {
                        setEditingVariableId(null);
                        setDraft(prev => ({
                          ...prev,
                          varLabel: '',
                          varKey: '',
                          varKeyTouched: false,
                          varDefault: '',
                          varHelp: '',
                        }));
                      }}
                    >
                      Cancel edit
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {variables.length === 0 && (
                    <p className="text-sm text-gray-500">No variables defined yet.</p>
                  )}
                  {variables.map(variable => (
                    <div
                      key={variable.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
                    >
                      <div>
                        <p className="font-semibold text-sm text-gray-800">
                          {variable.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {variable.key}
                          {variable.default_value ? ` · Default: ${variable.default_value}` : ''}
                        </p>
                        <p className="text-xs text-gray-400">
                          Scope: {variable.sourceGroupName || `Group ${variable.group_id}`}
                        </p>
                        {variable.help_text && (
                          <p className="text-xs text-gray-400">{variable.help_text}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200"
                          onMouseDown={preserveLayoutSelectionOnMouseDown}
                          onClick={() => insertVariable(variable.key)}
                        >
                          Insert
                        </button>
                        {canEditVariable(variable) && (
                          <>
                            <button
                              type="button"
                              className="text-xs px-2 py-1 rounded bg-white border border-slate-200 hover:border-slate-300"
                              onClick={() => {
                                setEditingVariableId(variable.id);
                                setDraft(prev => ({
                                  ...prev,
                                  varLabel: variable.label || '',
                                  varKey: variable.key || '',
                                  varKeyTouched: true,
                                  varDefault: variable.default_value || '',
                                  varHelp: variable.help_text || '',
                                }));
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                              onClick={() => deleteVariable(variable.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </details>
      </FormGroup>

      <FormGroup title="Batch generate">
        <details className="group rounded-2xl border border-slate-200 bg-white/80 p-4">
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Generate multiple templates</p>
                <p className="text-xs text-slate-500">
                  Fill values once, apply to selected templates.
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-500 transition group-open:rotate-180">
                ▾
              </span>
            </div>
          </summary>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              {selectedTemplates.length === 0 && (
                <p className="text-sm text-gray-500">
                  Select templates to see which variables you need to fill.
                </p>
              )}
              {selectedTemplates.length > 0 && usedVariables.length === 0 && (
                <p className="text-sm text-gray-500">
                  No placeholders found in the selected templates.
                </p>
              )}
              {usedVariables.map(variable => (
                <FormInput
                  key={variable.id || variable.key}
                  label={`${variable.label} (${variable.key})`}
                  value={batchValues[variable.key] ?? ''}
                  onChange={e =>
                    setBatchValues(prev => ({
                      ...prev,
                      [variable.key]: e.target.value,
                    }))
                  }
                  placeholder={variable.default_value || ''}
                />
              ))}
            </div>
            <div className="space-y-3">
              {selectedTemplates.some(t => getTemplateLanguageOptions(t).length > 0) && (
                <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
                  <p className="text-sm font-semibold text-slate-900">
                    Languages to generate
                  </p>
                  {selectedTemplates.map(template => {
                    const options = getTemplateLanguageOptions(template);
                    if (!options.length) return null;
                    const selectedCodes = batchLanguageSelections[String(template.id)] || [];
                    return (
                      <div
                        key={`lang-select-${template.id}`}
                        className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold text-slate-700">
                            {template.name}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-[11px] font-semibold text-slate-500 hover:text-primary"
                              onClick={() =>
                                setAllBatchLanguages(
                                  template.id,
                                  options.map(item => item.code)
                                )
                              }
                            >
                              All
                            </button>
                            <button
                              type="button"
                              className="text-[11px] font-semibold text-slate-500 hover:text-primary"
                              onClick={() => setAllBatchLanguages(template.id, [])}
                            >
                              None
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {options.map(option => (
                            <label
                              key={`${template.id}-${option.code}`}
                              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700"
                            >
                              <input
                                type="checkbox"
                                checked={selectedCodes.includes(option.code)}
                                onChange={() =>
                                  toggleBatchLanguage(template.id, option.code)
                                }
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                className="nav-button-primary w-full md:w-auto"
                onClick={handleBatchGenerate}
                disabled={!selectedTemplateIds.length}
              >
                Generate selected templates
              </button>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Save generated designs to event
                </label>
                <select
                  value={targetEventForSave || ''}
                  onChange={e => setTargetEventForSave(Number(e.target.value) || null)}
                  className="form-select w-full mb-2"
                >
                  <option value="">Choose event</option>
                  {visibleSelectableEvents.map(ev => (
                    <option key={ev.id} value={ev.id}>
                      {ev.name}
                      {ev.status === 'archived' ? ' (Archived)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="nav-button-secondary w-full"
                  onClick={openSaveDialog}
                  disabled={!generated.length}
                >
                  Choose base design
                </button>
              </div>
              <div className="space-y-2">
                {generated.map((item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <p className="font-semibold text-sm text-gray-800">{item.name}</p>
                    <pre className="mt-1 text-xs text-gray-700 whitespace-pre-wrap">
                      {item.content}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </details>
      </FormGroup>

      {saveDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Choose base design
                </h3>
                <p className="text-xs text-slate-500">
                  Generated text will be inserted into the selected design shell.
                </p>
              </div>
              <button
                type="button"
                onClick={closeSaveDialog}
                className="text-xs font-semibold text-slate-500 hover:text-slate-900"
                disabled={savingGenerated}
              >
                Close
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base design template
                </label>
                <select
                  value={selectedDesignId || ''}
                  onChange={e => setSelectedDesignId(Number(e.target.value) || null)}
                  className="form-select w-full"
                  disabled={designTemplatesLoading}
                >
                  <option value="">Select a design template</option>
                  {designTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} · {template.visibility}
                      {isBaseDesignTemplate(template) ? ' · Base design' : ''}
                    </option>
                  ))}
                </select>
                {designTemplatesLoading && (
                  <p className="mt-2 text-xs text-slate-500">Loading templates…</p>
                )}
                {!designTemplatesLoading && designTemplates.length === 0 && (
                  <p className="mt-2 text-xs text-slate-500">
                    No saved design templates found for this event.
                  </p>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                {generated.length} generated text templates will be saved to the event.
              </div>

              {saveSummary && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  {saveSummary.successCount} of {saveSummary.total} templates saved.
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  className="nav-button-secondary w-full sm:w-auto"
                  onClick={closeSaveDialog}
                  disabled={savingGenerated}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="nav-button-primary w-full sm:w-auto"
                  onClick={saveGeneratedDesigns}
                  disabled={
                    savingGenerated ||
                    !generated.length ||
                    !selectedDesignId ||
                    designTemplatesLoading
                  }
                >
                  {savingGenerated ? 'Saving…' : 'Create designs'}
                </button>
                {saveSummary && targetEventForSave && (
                  <button
                    type="button"
                    className="nav-button-secondary w-full sm:w-auto"
                    onClick={handleGoToEventTemplateOverview}
                    disabled={savingGenerated}
                  >
                    Open {targetEventName} templates
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {aiModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-lg">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  AI responses
                </h3>
                <p className="text-xs text-slate-500">
                  Last {aiResponses.length} responses. Select text to rewrite or copy.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAiModalOpen(false)}
                className="text-xs font-semibold text-slate-500 hover:text-slate-900"
              >
                Close
              </button>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[220px_1fr]">
              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  History
                </p>
                <div className="space-y-2">
                  {aiResponses.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No AI responses yet.
                    </p>
                  )}
                  {aiResponses.map(item => {
                    const isActive = item.id === activeAiResponse?.id;
                    const timestamp = new Date(item.createdAt).toLocaleTimeString();
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setActiveAiResponseId(item.id)}
                        className={`w-full rounded-lg border px-2 py-1.5 text-left text-xs transition ${
                          isActive
                            ? 'border-primary bg-white text-slate-900 shadow-sm'
                            : 'border-transparent bg-white/70 text-slate-600 hover:border-primary/40'
                        }`}
                      >
                        <div className="font-semibold text-slate-800">
                          {item.meta?.label || 'AI response'}
                        </div>
                        <div className="text-[10px] text-slate-400">{timestamp}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <textarea
                  ref={aiResponseRef}
                  value={activeAiResponse?.text || ''}
                  readOnly
                  className="min-h-[260px] w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 shadow-sm focus:outline-none"
                />
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs text-slate-500">
                    {activeAiResponse?.meta?.label
                      ? `Last action: ${activeAiResponse.meta.label}`
                      : 'Select text to rewrite.'}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                      onClick={handleCopyAiSelection}
                      disabled={!activeAiResponse}
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:border-amber-300"
                      onClick={handleRewriteSelection}
                      disabled={aiModalBusy || !activeAiResponse}
                    >
                      {aiModalBusy
                        ? 'Rewriting…'
                        : `Rewrite selection (${lastAiAction.label || 'Improve'})`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <NavButtons
        onPrev={() => onNavigate?.('contacts')}
        onNext={() => onNavigate?.('generate')}
      />
    </div>
  );
};

export default TextTemplatesSection;
